/* @flow */
/* eslint-env es6,browser */

/**
 * @description module for core utilities functions which doesn't have any dependency on others
 * @module utils/coreutils
 */

/**
 * @typedef {object} T
 */

/* eslint-disable no-empty-function */
/**
 * noop function
 * Utility function can be used for empty callback etc.,
 *
 * @returns {undefined}
 */
export function noop() {}
/* eslint-enable no-empty-function */

const objProto = Object.prototype;
// $FlowFixMe
const toStr: () => string = objProto.toString;

/**
 * Null check
 *
 * @param {T} obj  - Object to check
 * @returns {boolean} - Is given object is Null?
 */
export function isNull<T>(obj: T): boolean %checks {
  return toStr.call(obj) === '[object Null]';
}

/**
 * Undefined check
 *
 * @param {T} obj  - Object to check
 * @returns {boolean} - Is given object is undefined?
 */
export function isUndefined<T>(obj: T): boolean %checks {
  return toStr.call(obj) === '[object Undefined]';
}
/**
 * Valid object check
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it valid (Not null & not undefined)?
 */
export function isValid<T>(obj: T): boolean %checks {
  return !isNull(obj) && !isUndefined(obj);
}

/**
 * Boolean check
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - is valid oboolean object
 */
export function isBoolean<T>(obj: T): boolean %checks {
  // $FlowFixMe
  return isValid(obj) && (obj === true || obj === false);
}

/**
 * Checks if obj is function or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it a Function?
 */
export function isFunction<T>(obj: T): boolean %checks {
  return isValid(obj) && toStr.call(obj) === '[object Function]';
}

/**
 * Checks if obj is object or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it an Object?
 */
export function isObject<T>(obj: T): boolean %checks {
  return isValid(obj) && toStr.call(obj) === '[object Object]';
}

/**
 * Checks if obj is array or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it an Array?
 */
export function isArray<T>(obj: T): boolean %checks {
  return isValid(obj) && toStr.call(obj) === '[object Array]';
}

/**
 * Checks if obj is string or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it a String?
 */
export function isString<T>(obj: T): boolean %checks {
  return isValid(obj) && toStr.call(obj) === '[object String]';
}

/**
 * Checks if obj is number or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it a number?
 */
export function isNumber<T>(obj: T): boolean %checks {
  return isValid(obj) && toStr.call(obj) === '[object Number]';
}

/**
 *
 * @description Check for DOM element
 * @param {?HTMLElement} obj  Object to check
 * @returns {boolean} Check passes
 */
export function isDOMElement(obj: ?HTMLElement): boolean %checks {
  // Ref: https://stackoverflow.com/questions/384286/javascript-isdom-how-do-you-check-if-a-javascript-object-is-a-dom-object
  // DOM Level2 and then older browsers
  return 'HTMLElement' in window
    ? !!(obj && obj instanceof HTMLElement)
    : !!(obj && typeof obj === 'object' && obj.nodeType === 1 && obj.nodeName);
}

/**
 *
 * @description Check for DOM iframe element
 * @param {?HTMLIFrameElement} obj  Object to check
 * @returns {boolean} Check passes
 */
export function isHTMLIFrameElement(
  obj: ?HTMLElement | HTMLIFrameElement
): boolean %checks {
  // Ref: https://stackoverflow.com/questions/384286/javascript-isdom-how-do-you-check-if-a-javascript-object-is-a-dom-object
  // DOM Level2 and then older browsers
  return 'HTMLIFrameElement' in window
    ? !!(obj && obj instanceof HTMLIFrameElement)
    : !!(
        obj &&
        typeof obj === 'object' &&
        obj.nodeType === 1 &&
        obj.nodeName === 'IFRAME'
      );
}

/**
 * Removes duplicates in given array
 *
 * @param {...Array<T>} arr - Input array
 * @returns {Array<T>} - array with unique elements
 */
export function unique<T>(arr: Array<T> = []): Array<T> {
  if (!isArray(arr)) {
    return [];
  }
  return arr.filter((v, i, a) => a.indexOf(v) === i);
}

/**
 * Find intersection between two given arrays
 *
 * @param {Array<T>} first - 1st Array
 * @param {...Array<T>} rest - rest Array
 * @returns {Array<T>} - Intersection result
 */
export function intersect<T>(
  first: Array<T> = [],
  ...rest: Array<Array<T>>
): Array<T> {
  let ret = [];
  // input param check
  if (!isArray(first) || rest.some(arr => !isArray(arr))) {
    return ret;
  }
  // get unique value for the first array
  ret = unique(first);
  // go through each of the rest and find intersect values
  ret = rest.reduce(
    (result, currentArr) => result.filter(v => currentArr.includes(v)),
    ret
  );
  return ret;
}

/**
 * Find difference between given arrays. Only the unique elements
 * in the first array remains
 *
 * @param {Array<T>} first - 1st Array
 * @param {...Array<T>} rest - 2nd Array
 * @returns {Array<T>} - Result with difference
 */
export function difference<T>(
  first: Array<T> = [],
  ...rest: Array<Array<T>>
): Array<T> {
  let ret = [];
  if (!isArray(first) || rest.some(arr => !isArray(arr))) {
    return ret;
  }
  // get a copy of values from first array
  ret = [...first];
  // go through each of the rest and find values which are not included
  ret = rest.reduce(
    (result, currentArr) => result.filter(v => !currentArr.includes(v)),
    ret
  );
  return ret;
}

/**
 *
 * @param {Array<T>} arr - Array of elements to pick random one
 * @returns {T} Random element from array
 */
export function pickRandom<T>(arr: Array<T>): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * FisherYates shuffle
 * http://stackoverflow.com/a/6274398
 * https://bost.ocks.org/mike/shuffle/
 *
 * @param {Array<T>} arr - Array to be shuffled
 * @returns {Array<T>} - returns copy of shuffled array
 */
export function shuffle<T>(arr: Array<T>): Array<T> {
  // input arg check
  if (!isArray(arr)) {
    return [];
  }
  const array = [...arr];
  let counter = array.length;

  // while there are elements in the array
  // eslint-disable-next-line no-loops/no-loops
  while (counter > 0) {
    // pick a random index
    const index = Math.floor(Math.random() * counter);

    // decrease counter by 1
    counter -= 1;

    // and swap the last element with it
    const temp = array[counter];
    array[counter] = array[index];
    array[index] = temp;
  }

  return array;
}

const rbracket = /\[]$/;
/**
 * @global
 * @type {Function}
 * @callback paramAdd
 * @param {string} key
 * @param {string|function()} valOrFunction
 */
/**
 * Simplified version of PARAM serialize code from jquery
 *
 * @access private
 * @param {string} prefix - Prefix string
 * @param {(object|Array<*>|string|number)} o - child object to be serialized
 * @param {paramAdd} add - Callback fn
 * @returns {undefined}
 */
function buildParams(
  prefix: string,
  o: mixed,
  add: (string, string | (() => string)) => void
) {
  if (isObject(o)) {
    // iterate and add object values
    // $FlowFixMe
    Object.entries(o).forEach(([k, v]) => {
      buildParams(`${prefix}[${k}]`, v, add);
    });
  } else if (isArray(o)) {
    // iterate and add all array items
    // $FlowFixMe
    o.forEach((v: any, i: number) => {
      if (rbracket.test(prefix)) {
        // Treat each array item as a scalar.
        add(prefix, v);
      } else {
        buildParams(
          `${prefix}[${typeof v === 'object' && v !== null ? i : ''}]`,
          v,
          add
        );
      }
    });
  } else {
    // scalar item
    // $FlowFixMe
    add(prefix, o);
  }
}

/**
 * Utility funciton for building jsonp params
 *
 * @param {object} a - parameters
 * @returns {string} - Serialized param
 */
export function param(a: {} = {}): string {
  const s = [];
  const enc = encodeURIComponent;
  const add = (key: string, valueOrFunction: string | (() => string)) => {
    // If value is a function, invoke it and use its return value
    let value: string = '';
    // $FlowFixMe
    value = isFunction(valueOrFunction) ? valueOrFunction() : valueOrFunction;
    // $FlowFixMe
    s[s.length] = `${enc(key)}=${enc(value === null ? '' : value)}`;
  };
  if (isObject(a)) {
    Object.entries(a).forEach(([k, v]) => {
      buildParams(k, v, add);
    });
  }
  return s.join('&');
}

/**
 * @description Helper to build URL with query string
 * @param {string} url URL
 * @param {object}  p Query param key 7 values
 * @returns {string} Built URL
 */
export function buildUrl(url: string = '', p: {} = {}): string {
  let u = url;
  if (u.length === 0) {
    return u;
  }
  const paramString = param(p);
  if (paramString.length < 1) {
    return u;
  }
  // check and add
  // serialize data for adding to query param
  if (!u.includes('?')) {
    u += '?';
  }
  // if it doesn't ends with &, add it
  if (u[u.length - 1] !== '&') {
    u += '&';
  }
  u += paramString;
  return u;
}

/**
 * Convert cents to dollar
 *
 * @param {number} c - Cents to be converted
 * @returns {string} - Dollar value
 */
export function centsToDollar(c: number = 0): string {
  if (!isNumber(c)) {
    return '0.00';
  }
  return (c / 100).toFixed(2);
}

// flowlint unclear-type:off
/* eslint-disable no-loops/no-loops, no-restricted-syntax, no-cond-assign */
/**
 * jQuery extend functionality
 * Supports variable number of arguments
 *
 * @param {boolean|object} deepArg - Boolear for deep merge OR first object
 * @param {Array<object>} args - Remainint objects to be merged
 * @returns {object} - Extended result object
 */
export function extend(
  deepArg: boolean | Object,
  ...args: Array<Object>
): Object {
  let options;
  let name;
  let src;
  let copy;
  let copyIsArray;
  let clone;
  let target = deepArg || {};
  let i = 0;
  let deep = false;

  // eslint-disable-next-line prefer-destructuring
  const length = args.length;
  // Handle a deep copy situation
  if (typeof target === 'boolean') {
    deep = target;
    target = args[0] || {};
    // skip the boolean and the target
    i = 1;
  }

  // Handle case when target is a string or something (possible in deep copy)
  if (typeof target !== 'object' && !isFunction(target)) {
    target = {};
  }

  for (; i < length; i += 1) {
    // Only deal with non-null/undefined values
    if ((options = args[i]) !== null) {
      // Extend the base object
      /* eslint-disable no-continue */
      for (name in options) {
        // $FlowFixMe
        if (!Object.prototype.hasOwnProperty.call(options, name)) {
          continue; // ignore inherited properties
        }
        src = target[name];
        copy = options[name];

        // Prevent never-ending loop
        if (target === copy) {
          continue;
        }
        /* eslint-enable no-continue */
        // Recurse if we're merging plain objects or arrays
        if (deep && copy && (isObject(copy) || (copyIsArray = isArray(copy)))) {
          // flowlint-next-line sketchy-null-bool:off
          if (copyIsArray) {
            copyIsArray = false;
            clone = src && isArray(src) ? src : [];
          } else {
            clone = src && isObject(src) ? src : {};
          }

          // Never move original objects, clone them
          target[name] = extend(deep, clone, copy);

          // Don't bring in undefined values
        } else if (copy !== undefined) {
          target[name] = copy;
        }
      }
    }
  }
  // Return the modified object
  return target;
}
/* eslint-enable no-loops/no-loops, no-restricted-syntax, no-cond-assign */
// flowlint unclear-type:error
